import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import * as toDoActions from '../../../features/todos/actions';
import * as authActions from '../../../features/auth/actions';
import * as fromActions from '../../../features/legal-entities/actions';
import * as fromToDoActions from '../../../features/todos/actions';
import * as sharedActions from '../../../shared/actions';
import { fromGetBusinessDetailsActions, clearGetProofBusinessFicaDocs } from '../../../features/businesses-detail/actions';
import { AnimationOptions } from 'ngx-lottie';
import { SharedService } from '../../shared.service';

import * as toDoSelector from '../../../features/todos/selectors';
import * as fromPaymentsActions from '../../../features/payment-runs/actions';
import { RouteURL, SessionStorageIds } from '../../enums';
import { AdviserLandingService } from '../../../core/services/adviser-landing.service';
import * as fromAllUsersActions from '../../../features/legal-entities/actions/all-users.actions';
import { IBusinessUserRolesRequest } from '../../../features/auth/models/business-user-roles.model';
import {ILegalEntitiesBusinessesResponse} from "../../models/shared.model";
import { Router } from '@angular/router';
import { LegalEntityHistoryService } from '../../legal-entity-history.service';
import { PermissionService } from '../../../core/services/permission.service';
import { EMPTY_GUID } from '../../constants';
import { selectLegalEntities } from '../../selectors/legal-entities.selectors';

@Component({
  selector: 'app-search-legal-entities',
  templateUrl: './search-legal-entities.component.html',
  styleUrls: ['./search-legal-entities.component.css']
})
export class SearchLegalEntitiesComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  selectedEntity: ILegalEntitiesBusinessesResponse;
  searchLegalEntity: string;
  showScreenType: string = 'list'; // "list", "add", "message"
  options: AnimationOptions = {
    path: '../assets/Lottie/hayrick-loader.json'
  };
  selectedLegalEntity$: Observable<ILegalEntitiesBusinessesResponse>;
  messageText: string = '';
  legalEntities: any[];
  filteredEntities: any[];
  @Output() closePopupEvent = new EventEmitter<void>();

  constructor(
    private store: Store<any>,
    private sharedService: SharedService,
    private router: Router,
    private legalEntityHistoryService: LegalEntityHistoryService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    const cachedEntities = this.getCachedLegalEntities();
    if (cachedEntities.length > 0) {
      this.setLegalEntities(cachedEntities);
    }

    this.subscription.add(
      this.store.select(selectLegalEntities).subscribe(entities => {
        if (entities?.length) {
          this.setLegalEntities(entities);
        }
      })
    );

    this.subscription.add(
      this.store.select(toDoSelector.selectLegalEntitiesBusinesses).subscribe(entities => {
        if (entities?.length) {
          this.setLegalEntities(entities);
        }
      })
    );

    this.store.dispatch(sharedActions.loadLegalEntities({ param: '', noLoader: true }));
    this.store.dispatch(toDoActions.loadBusinesses({ param: '', noLoader: true }));
  }

  private getCachedLegalEntities(): ILegalEntitiesBusinessesResponse[] {
    const raw = sessionStorage.getItem(SessionStorageIds.LEGAL_ENTITIES);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private setLegalEntities(entities: ILegalEntitiesBusinessesResponse[]): void {
    this.legalEntities = entities;
    this.filteredEntities = [...entities];
    sessionStorage.setItem(SessionStorageIds.LEGAL_ENTITIES, JSON.stringify(entities));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  searchEntity(event: any) {
    const inputElement = event.event.currentTarget as HTMLInputElement;
    const searchTerm = inputElement.value.trim().toLowerCase();
    this.filterListbox(searchTerm);
  }

  filterListbox(searchTerm: string) {
    if (!this.legalEntities?.length) {
      this.filteredEntities = [];
      return;
    }

    if (searchTerm) {
      this.filteredEntities = this.legalEntities.filter(le =>
        le.legalEntityName.toLowerCase().includes(searchTerm)
      );
    } else {
      this.filteredEntities = [...this.legalEntities];
    }
  }


  selectedLegalEntity(e) {
    this.selectedEntity = e.itemData;

    // Save to history
    this.legalEntityHistoryService.addToHistory(this.selectedEntity);

    sessionStorage.setItem('IsFromToDo', 'false');
    this.sharedService.isFromLegalEntities = true;
    this.store.dispatch(toDoActions.loadRegulationVerified({ businessId: this.selectedEntity.legalEntityId }));
    this.store.dispatch(fromActions.clearBusinessSelected());
    this.store.dispatch(fromActions.clearAssetManagerViewLinkedBusinesses());
    this.store.dispatch(fromActions.loadClearManageOfferings());

    // Set sessionStorage before store dispatches so side nav and other subscribers see the new entity when they react to the store
    sessionStorage.setItem('businessId', this.selectedEntity.legalEntityId);
    sessionStorage.setItem('legalEntity', JSON.stringify(this.selectedEntity));
    this.updatePermissionsForLegalEntity(this.selectedEntity.legalEntityId);

    this.store.dispatch(
      fromActions.saveSelectedBusinessId({
        save: this.selectedEntity.legalEntityId
      })
    );
    this.store.dispatch(
      fromToDoActions.loadSelectedLegalEntity({
        selectedLE: this.selectedEntity
      })
    );

    this.store.dispatch(fromGetBusinessDetailsActions.clearGetBusinessDetailsResponse());
    this.store.dispatch(clearGetProofBusinessFicaDocs());

    const userRolesReq: IBusinessUserRolesRequest = {
                            businessId: this.selectedEntity.legalEntityId,
                            token: sessionStorage.getItem('token') || '',
                            uniqueDeviceId: EMPTY_GUID
                          };
    this.store.dispatch(authActions.loadBusinessUserRoles({ request: userRolesReq }));

    this.store.dispatch(
      fromAllUsersActions.getBusiness({
        businessId: this.selectedEntity.legalEntityId
      })
    );
    // Defer close and navigation to next tick so store/session updates are applied first.
    // Fixes first selection not working (dashboard not opening until second selection).
    setTimeout(() => {
      this.closePopup();
      this.router.navigate([AdviserLandingService.legalEntityHomeRoute()]);
    }, 0);
  }

  addLegalEntity() {
    this.showScreenType = 'add';
  }

  hideAddLegalEntitiesPopup(){
    this.showScreenType = this.messageText ? 'message' : 'list';
  }

  showCenteredMessage() {
    this.showScreenType = 'message';
  }

  onCreateEntityMessage(event: any) {
    this.messageText = event.message;
    this.selectedEntity = event.legalEntity;
    sessionStorage.setItem('legalEntity', JSON.stringify(this.selectedEntity));
  }

  closePopup() {
    this.showScreenType = 'list';
    this.closePopupEvent.emit();
   }

  private updatePermissionsForLegalEntity(legalEntityId: string): void {
    const stored = sessionStorage.getItem(SessionStorageIds.AUTH_LEGAL_ENTITY_PERMISSIONS);
    const legalEntityPermissions = stored ? (JSON.parse(stored) as any[]) : [];
    const current = legalEntityPermissions.find(entry => entry.legalEntityId === legalEntityId);
    sessionStorage.setItem(SessionStorageIds.AUTH_PERMISSIONS, JSON.stringify(current?.permissions || []));
    sessionStorage.setItem(SessionStorageIds.AUTH_CURRENT_LEGAL_ENTITY_ID, legalEntityId);
    this.permissionService.refreshFromSession();
  }

  goToLegalEntityOverview() {
    sessionStorage.setItem('IsFromToDo', 'false');
    this.sharedService.isFromLegalEntities = true;

    // Set sessionStorage before store dispatches so side nav and other subscribers see the new entity when they react to the store
    sessionStorage.setItem('businessId', this.selectedEntity.legalEntityId);
    sessionStorage.setItem('legalEntity', JSON.stringify(this.selectedEntity));
    this.updatePermissionsForLegalEntity(this.selectedEntity.legalEntityId);

    this.store.dispatch(toDoActions.loadRegulationVerified({ businessId: this.selectedEntity.legalEntityId }));
    this.store.dispatch(fromActions.clearBusinessSelected());
    this.store.dispatch(fromActions.clearAssetManagerViewLinkedBusinesses());
    this.store.dispatch(fromActions.loadClearManageOfferings());
    this.store.dispatch(
      fromActions.saveSelectedBusinessId({
        save: this.selectedEntity.legalEntityId
      })
    );
    this.store.dispatch(
      fromToDoActions.loadSelectedLegalEntity({
        selectedLE: this.selectedEntity
      })
    );

    this.store.dispatch(fromGetBusinessDetailsActions.clearGetBusinessDetailsResponse());
    this.store.dispatch(clearGetProofBusinessFicaDocs());

    const userRolesReq: IBusinessUserRolesRequest = {
                            businessId: this.selectedEntity.legalEntityId,
                            token: sessionStorage.getItem('token') || '',
                            uniqueDeviceId: EMPTY_GUID
                          };
    this.store.dispatch(authActions.loadBusinessUserRoles({ request: userRolesReq }));

    this.store.dispatch(
      fromAllUsersActions.getBusiness({
        businessId: this.selectedEntity.legalEntityId
      })
    );
    this.closePopup();
    this.showScreenType = 'list';
    this.router.navigate([RouteURL.LEGAL_ENTITY_OVERVIEW]);
  }
}
